import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  addNewAppointment,
  getAllDoctor,
  getAllPatient,
  getAllAppointment,
  getSingleDoctor,
  getAllConsultant,
  END_POINT,
} from '../../Redux/AdminReducer/action';
import CustomSpinner from '../../Components/CommonComponents/CustomSpinner';
import SelectInput from '../../Components/CommonComponents/SelectInput';
import CustomButton from '../../Components/CommonComponents/CustomButton';
import CustomBreadcrumbs from '../../Components/CommonComponents/CustomBreadcrumbs';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CalenderAndTimeSlot, {
  formatDateKey,
  formatDisplayDate,
} from '../../Components/CommonComponents/CustomAppointmentDateCalender';
import axios from 'axios';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { FiCalendar, FiClock, FiVideo, FiMapPin, FiCheckCircle, FiUser } from 'react-icons/fi';

const fetchItems = async (path) => {
  try {
    const result = await axios.get(`${END_POINT}/${path}`);
    return result;
  } catch (error) {
    toast.error(error?.response?.data || 'Something went wrong');
    console.error(error);
  }
};

const initialState = {
  doctor: null,
  patient: null,
  bookDate: formatDateKey(new Date()),
  bookTimeSlot: '',
  selectedRole: 'doctor',
  online: false,
  IsOnline: false,
  IsDoctor: true,
  // Booking provenance: bookings made here default to the clinic; channel
  // 'auto' means "derive from the consultation mode".
  bookingLocation: 'clinic',
  channel: 'auto',
};

const BOOKING_LOCATION_OPTIONS = [
  { value: 'clinic', label: 'Clinic' },
  { value: 'outside', label: 'Outside Clinic' },
];

const CHANNEL_OPTIONS = [
  { value: 'online', label: 'Online' },
  { value: 'in-clinic', label: 'In Clinic' },
];

// Same defaults the server applies when no channel is chosen.
const deriveChannel = (form) => (form?.online ? 'online' : 'in-clinic');

const isChannelOption = (value) => CHANNEL_OPTIONS.some((option) => option.value === value);

export default function AddAppointment() {
  const {
    addAppointmentMessage,
    addAppointmentFail,
    addAppointmentSuccess,
    addAppointmentProcessing,
    getAllPatientData,
    getAllPatientSuccess,
    getAllDoctorData,
    getAllPatientProcessing,
    getAllDoctorProcessing,
    getAllAppointmentData,
    getAllConsultantData,
    getAllConsultantProcessing,
  } = useSelector((state) => state.AdminReducer);

  const [query, setQuery] = useState({ search: '', page: 1, pageSize: 200 });
  const [formData, setFormData] = useState(initialState);
  // Form error starts empty to avoid false error borders and "doctor" error text on load
  const [formError, setFormError] = useState({});
  const [assistantDoctor, setAssistantDoctor] = useState([]);
  const [mainDoctorData, setMainDoctorData] = useState([]);
  const userLogin = useSelector((state) => state.AuthReducer);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const passedPatientId = location.state?.patientId || searchParams.get('patientId');
  const passedPatientData = location.state?.patientData;
  const passedDoctorData = location.state?.doctorData;

  const dispatch = useDispatch();

  useEffect(() => {
    const fetchData = async () => {
      dispatch(getAllPatient(query));
      switch (formData?.selectedRole) {
        case 'mainDoctor':
          try {
            fetchItems('mainDoctor/profile').then((e) => {
              if (e?.data) {
                setMainDoctorData([e.data]);
              }
            });
          } catch (error) {
            console.error('Error fetching main doctor:', error);
          }
          break;
        case 'consultant':
          dispatch(getAllConsultant(query));
          break;
        case 'assistantDoctor':
          try {
            fetchItems('assistantDoctor').then((e) => {
              setAssistantDoctor(e?.data || []);
            });
          } catch (error) {
            toast.error(error?.response?.data || 'Something went wrong');
            console.error('Error fetching assistant doctor:', error);
          }
          break;
        default:
          dispatch(getAllDoctor(query));
      }
    };

    fetchData();
  }, [formData?.selectedRole, query, dispatch]);

  useEffect(() => {
    if (formData?.doctor?.id) {
      dispatch(getAllAppointment({ search: formData.doctor.id, page: 1, limit: 100 }));
    }
  }, [formData?.doctor?.id, dispatch]);

  useEffect(() => {
    if (passedPatientData?._id) {
      setFormData((prev) => {
        if (prev.patient?.id === passedPatientData._id) return prev;
        return {
          ...prev,
          patient: {
            id: passedPatientData._id,
            name: passedPatientData.firstName,
            surname: passedPatientData.surname || '',
            phone: passedPatientData.mobile || '',
          },
        };
      });
    } else if (passedPatientId) {
      const found = getAllPatientData?.find((p) => p._id === passedPatientId);
      if (found) {
        setFormData((prev) => {
          if (prev.patient?.id === found._id) return prev;
          return {
            ...prev,
            patient: {
              id: found._id,
              name: found.firstName,
              surname: found.surname || '',
              phone: found.mobile || '',
            },
          };
        });
      } else {
        fetchItems(`customer/${passedPatientId}`)
          .then((res) => {
            if (res?.data) {
              const p = res.data;
              setFormData((prev) => {
                if (prev.patient?.id === p._id) return prev;
                return {
                  ...prev,
                  patient: {
                    id: p._id,
                    name: p.firstName,
                    surname: p.surname || '',
                    phone: p.mobile || '',
                  },
                };
              });
            }
          })
          .catch((err) => console.error('Error fetching patient for appointment:', err));
      }
    }
  }, [passedPatientId, passedPatientData, getAllPatientData]);

  const patientOptions = useMemo(() => {
    const rawList = Array.isArray(getAllPatientData)
      ? getAllPatientData
      : Array.isArray(getAllPatientData?.data)
      ? getAllPatientData.data
      : [];

    const options = rawList.map((p) => ({
      value: p._id,
      label: `${p.firstName || ''} ${p.surname || ''}`.trim() + (p.customerId ? ` (${p.customerId})` : ''),
    }));

    if (formData.patient?.id && !options.some((opt) => opt.value === formData.patient.id)) {
      options.unshift({
        value: formData.patient.id,
        label: `${formData.patient.name || ''} ${formData.patient.surname || ''}`.trim(),
      });
    }

    return options;
  }, [getAllPatientData, formData.patient]);

  useEffect(() => {
    if (addAppointmentSuccess && !addAppointmentProcessing) {
      toast.success(addAppointmentMessage || 'Appointment added successfully!', {
        position: toast.POSITION.TOP_RIGHT,
      });

      setFormData(initialState);
      setFormError({});
      navigate('/appointment/list');
    }
    if (addAppointmentFail && !addAppointmentProcessing) {
      toast.error(addAppointmentMessage || 'Failed to add appointment', {
        position: toast.POSITION.TOP_RIGHT,
      });
    }
  }, [addAppointmentProcessing, addAppointmentSuccess, addAppointmentFail, addAppointmentMessage, navigate]);

  const handelButtonClick = () => {
    let isValidInput = true;
    const updatedFormError = {};

    if (!formData.doctor?.id) {
      updatedFormError.doctor = `Please select a ${formData?.selectedRole || 'doctor'}`;
      isValidInput = false;
    }

    if (!formData.patient?.id) {
      updatedFormError.patient = 'Please select a patient';
      isValidInput = false;
    }

    if (!formData.bookDate) {
      updatedFormError.bookDate = 'Please select an appointment date';
      isValidInput = false;
    }

    if (!formData.bookTimeSlot?._id && !formData.bookTimeSlot?.startTime) {
      updatedFormError.bookTimeSlot = 'Please select an available time slot';
      isValidInput = false;
    }

    const payload = { ...formData };

    if (formData.selectedRole === 'consultant') {
      payload.IsConsultant = true;
      payload.IsDoctor = false;
      payload.IsAssistantDoctor = false;
    } else if (formData.selectedRole === 'assistantDoctor') {
      payload.IsAssistantDoctor = true;
      payload.IsDoctor = false;
      payload.IsConsultant = false;
    } else {
      payload.IsDoctor = true;
      payload.IsConsultant = false;
      payload.IsAssistantDoctor = false;
    }

    payload.IsOnline = Boolean(formData.online);
    payload.online = Boolean(formData.online);
    payload.bookDate = formatDateKey(formData.bookDate);
    payload.bookingLocation = formData.bookingLocation || 'clinic';
    payload.channel = isChannelOption(formData.channel) ? formData.channel : deriveChannel(formData);
    payload.sourcePage = 'admin-add-appointment';

    setFormError(updatedFormError);

    if (!isValidInput) {
      Object.values(updatedFormError).forEach((msg) => {
        toast.error(msg);
      });
      return;
    }

    dispatch(addNewAppointment(payload));
  };

  useEffect(() => {
    let selectedDoctorList = [];

    if (!['MainDoctor', 'Receptionist'].includes(userLogin?.userLogindata?.data?.role)) {
      const userlog = userLogin?.userLogindata?.data;
      const { name, _id } = userlog || {};
      setFormData((prev) => ({ ...prev, doctor: { id: _id, name: name } }));
      return;
    }

    switch (formData?.selectedRole) {
      case 'mainDoctor': {
        const raw = mainDoctorData;
        selectedDoctorList = Array.isArray(raw) ? raw : [];
        if (selectedDoctorList.length > 0) {
          const { _id: mainDocId, name: mainDocName } = selectedDoctorList[0];
          setFormData((prev) => ({
            ...prev,
            doctor: prev.doctor?.id ? prev.doctor : { id: mainDocId, name: mainDocName },
          }));
        }
        break;
      }

      case 'consultant': {
        const raw = getAllConsultantData;
        selectedDoctorList = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
        if (selectedDoctorList.length > 0) {
          const { _id: consultantId, name: consultantName } = selectedDoctorList[0];
          setFormData((prev) => ({
            ...prev,
            doctor: prev.doctor?.id ? prev.doctor : { id: consultantId, name: consultantName },
          }));
        }
        break;
      }

      case 'assistantDoctor': {
        const raw = assistantDoctor;
        selectedDoctorList = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
        if (selectedDoctorList.length > 0) {
          const { _id: assistantId, name: assistantName } = selectedDoctorList[0];
          setFormData((prev) => ({
            ...prev,
            doctor: prev.doctor?.id ? prev.doctor : { id: assistantId, name: assistantName },
          }));
        }
        break;
      }

      default: {
        const raw = getAllDoctorData;
        selectedDoctorList = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
        if (selectedDoctorList.length > 0) {
          const targetDoctorId = passedDoctorData?._id || formData?.doctor?.id;
          const matchedDoctor = targetDoctorId && selectedDoctorList.find((d) => d && d._id === targetDoctorId);
          const { _id: defaultId, name: defaultName } = matchedDoctor || selectedDoctorList[0] || {};
          setFormData((prev) => {
            if (prev.doctor?.id === defaultId) return prev;
            return { ...prev, doctor: { id: defaultId, name: defaultName } };
          });
        }
      }
    }
  }, [
    getAllDoctorData,
    getAllConsultantData,
    assistantDoctor,
    mainDoctorData,
    formData?.selectedRole,
    passedDoctorData,
    userLogin?.userLogindata?.data,
  ]);

  const activeDoctorList = useMemo(() => {
    let list = [];
    if (formData.selectedRole === 'mainDoctor') {
      list = Array.isArray(mainDoctorData) ? mainDoctorData : [];
    } else if (formData.selectedRole === 'consultant') {
      const c = getAllConsultantData;
      list = Array.isArray(c) ? c : Array.isArray(c?.data) ? c.data : [];
    } else if (formData.selectedRole === 'assistantDoctor') {
      const a = assistantDoctor;
      list = Array.isArray(a) ? a : Array.isArray(a?.data) ? a.data : [];
    } else {
      const d = getAllDoctorData;
      list = Array.isArray(d) ? d : Array.isArray(d?.data) ? d.data : [];
    }
    return Array.isArray(list) ? list : [];
  }, [formData.selectedRole, mainDoctorData, getAllDoctorData, getAllConsultantData, assistantDoctor]);

  const activeSelectedDoctor = useMemo(() => {
    if (!Array.isArray(activeDoctorList)) return [];
    return activeDoctorList.filter((d) => d && d._id === formData?.doctor?.id);
  }, [activeDoctorList, formData?.doctor?.id]);

  const isRoleSelectable = ['MainDoctor', 'Receptionist'].includes(
    userLogin?.userLogindata?.data?.role
  );

  return (
    <div className="min-h-screen bg-gray-50/70 w-full px-4 md:px-8 py-6">
      {/* Breadcrumbs */}
      <div className="mb-4">
        <CustomBreadcrumbs
          data={[
            { title: 'Dashboard', url: '/dashboard' },
            { title: 'Add Appointment' },
          ]}
        />
      </div>

      {getAllPatientProcessing ||
      getAllDoctorProcessing ||
      getAllConsultantProcessing ||
      !getAllPatientSuccess ? (
        <CustomSpinner />
      ) : (
        <div className="w-full">
          {/* Main Card */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            {/* Header */}
            <div className="px-6 py-5 border-b border-gray-100 bg-gradient-to-r from-slate-50 to-white flex flex-wrap items-center justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900 tracking-tight">
                  Add Appointment
                </h2>
                <p className="text-xs text-gray-500 mt-1">
                  Select doctor, patient, date, and appointment slot
                </p>
              </div>

              {/* Consultation Mode Toggle Pills */}
              <div className="flex items-center gap-1.5 p-1 bg-gray-100 rounded-xl border border-gray-200 text-xs">
                <button
                  type="button"
                  onClick={() =>
                    setFormData((prev) => ({
                      ...prev,
                      online: false,
                      IsOnline: false,
                      bookTimeSlot: '',
                    }))
                  }
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-semibold transition cursor-pointer ${
                    !formData.online
                      ? 'bg-white text-emerald-700 shadow-xs'
                      : 'text-gray-500 hover:text-gray-900'
                  }`}
                >
                  <FiMapPin className="w-3.5 h-3.5" />
                  <span>In-Clinic</span>
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setFormData((prev) => ({
                      ...prev,
                      online: true,
                      IsOnline: true,
                      bookTimeSlot: '',
                    }))
                  }
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-semibold transition cursor-pointer ${
                    formData.online
                      ? 'bg-white text-sky-700 shadow-xs'
                      : 'text-gray-500 hover:text-gray-900'
                  }`}
                >
                  <FiVideo className="w-3.5 h-3.5" />
                  <span>Online Video</span>
                </button>
              </div>
            </div>

            {/* Form Body */}
            <div className="p-6 space-y-6">
              {/* Doctor & Patient Selection Controls */}
              <div
                className={`grid grid-cols-1 gap-4 ${
                  isRoleSelectable ? 'md:grid-cols-3' : 'md:grid-cols-2'
                }`}
              >
                {/* Role (if permitted) */}
                {isRoleSelectable && (
                  <SelectInput
                    options={[
                      { value: 'mainDoctor', label: 'Main Doctor' },
                      { value: 'doctor', label: 'Doctor' },
                      { value: 'consultant', label: 'Consultant' },
                      { value: 'assistantDoctor', label: 'Assistant Doctor' },
                    ]}
                    value={formData.selectedRole}
                    onChange={(e) => {
                      setFormData((prev) => ({
                        ...prev,
                        selectedRole: e.target.value,
                        doctor: null,
                        bookTimeSlot: '',
                      }));
                      setFormError((prev) => ({ ...prev, selectedRole: '', doctor: '' }));
                    }}
                    placeholder="Select Role"
                    label="Select Role"
                    name="selectedRole"
                    error={formError.selectedRole}
                  />
                )}

                {/* Doctor Selection */}
                <SelectInput
                  options={activeDoctorList.map((doc) => ({
                    value: doc._id,
                    label: doc.name,
                  }))}
                  value={formData?.doctor?.id || ''}
                  onChange={(e) => {
                    const selected = activeDoctorList.find((i) => i._id === e.target.value);
                    if (selected) {
                      setFormData((prev) => ({
                        ...prev,
                        doctor: { id: selected._id, name: selected.name },
                        bookTimeSlot: '',
                      }));
                      setFormError((prev) => ({ ...prev, doctor: '' }));
                    }
                  }}
                  placeholder={`Select ${
                    formData.selectedRole === 'consultant'
                      ? 'Consultant'
                      : formData.selectedRole === 'assistantDoctor'
                      ? 'Assistant Doctor'
                      : 'Doctor'
                  }`}
                  label={`Select ${
                    formData.selectedRole === 'consultant'
                      ? 'Consultant'
                      : formData.selectedRole === 'assistantDoctor'
                      ? 'Assistant Doctor'
                      : 'Doctor'
                  }`}
                  name="doctor"
                  error={formError.doctor}
                />

                {/* Patient Selection */}
                <SelectInput
                  options={patientOptions}
                  value={formData.patient?.id || ''}
                  onChange={(e) => {
                    const selected =
                      (getAllPatientData || []).find((i) => i._id === e.target.value) ||
                      (formData.patient?.id === e.target.value
                        ? {
                            _id: formData.patient.id,
                            firstName: formData.patient.name,
                            surname: formData.patient.surname,
                            mobile: formData.patient.phone,
                          }
                        : null);

                    if (selected) {
                      setFormData((prev) => ({
                        ...prev,
                        patient: {
                          id: selected._id,
                          name: selected.firstName,
                          surname: selected.surname,
                          phone: selected.mobile,
                        },
                      }));
                      setFormError((prev) => ({ ...prev, patient: '' }));
                    }
                  }}
                  placeholder="Select Patient"
                  label="Select Patient"
                  name="patient"
                  error={formError.patient}
                />
              </div>

              {/* Booking Location & Channel */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <SelectInput
                  options={BOOKING_LOCATION_OPTIONS}
                  value={formData.bookingLocation || 'clinic'}
                  onChange={(e) => setFormData((prev) => ({ ...prev, bookingLocation: e.target.value }))}
                  placeholder="Booking Location"
                  label="Booking Location"
                  name="bookingLocation"
                />
                <SelectInput
                  options={CHANNEL_OPTIONS}
                  value={isChannelOption(formData.channel) ? formData.channel : deriveChannel(formData)}
                  onChange={(e) => setFormData((prev) => ({ ...prev, channel: e.target.value }))}
                  placeholder="Channel"
                  label="Channel"
                  name="channel"
                />
              </div>

              {/* Calendar & Time Slots */}
              <div className="pt-2">
                <CalenderAndTimeSlot
                  selectedDoctor={activeSelectedDoctor}
                  formData={formData}
                  setFormData={setFormData}
                  getAllAppointmentData={getAllAppointmentData}
                  isOnlineFilter={formData.online}
                />
              </div>

              {/* Selected Booking Summary */}
              {formData.bookTimeSlot && formData.bookDate && (
                <div className="p-4 rounded-xl bg-gradient-to-r from-sky-50 via-blue-50 to-indigo-50 border border-sky-200 flex flex-wrap items-center justify-between gap-3 shadow-2xs">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-sky-600 text-white flex items-center justify-center font-bold shadow-xs">
                      <FiCheckCircle className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-slate-800">
                        {formData.patient?.name
                          ? `${formData.patient.name} ${formData.patient.surname || ''}`
                          : 'Patient Selected'}
                      </h4>
                      <p className="text-xs text-slate-600 mt-0.5">
                        Consultation with{' '}
                        <strong className="text-sky-700">{formData.doctor?.name || 'Doctor'}</strong> ·{' '}
                        {formatDisplayDate(formData.bookDate)} at{' '}
                        <strong>
                          {formData.bookTimeSlot.startTime} – {formData.bookTimeSlot.endTime}
                        </strong>{' '}
                        ({formData.online ? 'Online Video' : 'In-Clinic'})
                      </p>
                    </div>
                  </div>
                  <span className="px-3 py-1 bg-sky-600 text-white text-xs font-semibold rounded-lg shadow-2xs">
                    Ready to Book
                  </span>
                </div>
              )}
            </div>

            {/* Footer with Submit Button */}
            <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-end">
              <CustomButton
                isProcessing={addAppointmentProcessing}
                onClick={handelButtonClick}
                label="Add Appointment"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
